/* eslint-disable */

// `domainId` is not always available: Layout resolves it asynchronously from /api/v1/me/, and an
// account without domains never gets one. Domain-scoped links are built with this placeholder
// segment so `redirectDomainLinks` can recognise and rewrite them instead of emitting links to
// `/domains/null/...`.
const NO_DOMAIN = '__no_domain__';

// Where a domain-scoped link points when there is no domain: the settings page, where a domain
// can be connected.
const DOMAIN_FALLBACK_HREF = '/settings/';

function redirectDomainLinks(items) {
    return items.map((item) => {
        const isDomainScoped =
            item.href?.includes(NO_DOMAIN) ||
            item.pathnames?.some((pathname) => pathname.includes(NO_DOMAIN));

        return {
            ...item,
            ...(isDomainScoped && {
                href: DOMAIN_FALLBACK_HREF,
                // An empty `pathnames` keeps the item out of the active-item lookup in Sidebar /
                // TopMenu (both check `pathnames` before `href`), so a rewritten item does not
                // highlight itself while the user is actually on the settings page.
                pathnames: [],
            }),
            ...(item.children && { children: redirectDomainLinks(item.children) }),
        };
    });
}

export const options = ({ domainId, t = () => {} }) => {
    const domainSegment = domainId || NO_DOMAIN;

    let data = [
        {
            id: 'allDomain',
            clearAllSelected: true,
            title: t('layout.menuItems.all_domains'),
            href: '/domains/',
            icon: 'icon-fi_grid-converted',
        },
        {
            id: 'ai_tracking',
            title: t('layout.menuItems.ai_tracking'),
            icon: 'icon-ai-tracking',
            collapsTargetId: 'collapseAiTracking',
            clearAllSelected: true,
            children: [
                {
                    id: 'discover_tracking_ai_tracking',
                    title: t('layout.menuItems.keyword_manager'),
                    href: `/ai-tracking/`,
                    icon: 'icon-ai-keyword',
                    parent: 'ai_tracking',
                },
                // {
                //     id: 'discover_tracking_ai_tracking_projects',
                //     title: t('layout.menuItems.project_manager'),
                //     href: `/ai-tracking/projects/`,
                //     icon: 'icon-ai-projects',
                //     parent: 'ai_tracking',
                // },
                // {
                //     id: 'discover_tracking_ai_tracking_add',
                //     title: t('layout.menuItems.add_keyword'),
                //     href: `/ai-tracking/add/`,
                //     icon: 'icon-ai-new-keyword',
                //     parent: 'ai_tracking',
                // },
                {
                    id: 'discover_tracking_ai_tracking_prompt_generator',
                    title: t('layout.menuItems.prompt_generator'),
                    href: `/ai-tracking/prompt-generator/`,
                    icon: 'icon-prompt-generator',
                    parent: 'ai_tracking',
                },
                {
                    id: 'discover_tracking_ai_tracking_prompt_history',
                    title: t('layout.menuItems.prompt_history'),
                    href: `/ai-tracking/prompt-history/`,
                    icon: 'icon-ai-projects',
                    parent: 'ai_tracking',
                },
            ],
        },
        {
            id: 'discover_analytics',
            title: t('layout.menuItems.discover_analytics'),
            icon: 'icon-iconly_svg_converted-converted',
            collapsTargetId: 'collapseDiscoverAnalytics',
            clearAllSelected: true,
            requiredDomain: true,
            children: [
                {
                    id: 'discover_analytics_overview',
                    title: t('layout.menuItems.overview'),
                    href: `/domains/${domainSegment}/`,
                    icon: 'icon-pie-chart',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_categories',
                    title: t('layout.menuItems.categories'),
                    pathnames: [
                        `/domains/${domainSegment}/categories/`,
                        `/domains/${domainSegment}/categories/category/`,
                    ],
                    href: `/domains/${domainSegment}/categories/`,
                    icon: 'icon-fi_grid',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_entities',
                    title: t('layout.menuItems.entities'),
                    pathnames: [
                        `/domains/${domainSegment}/entities/`,
                        `/domains/${domainSegment}/entities/entity/`,
                    ],
                    href: `/domains/${domainSegment}/entities/`,
                    icon: 'icon-building-06',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_pages',
                    title: t('layout.menuItems.pages'),
                    pathnames: [
                        `/domains/${domainSegment}/pages/`,
                        `/domains/${domainSegment}/pages/page/`,
                    ],
                    href: `/domains/${domainSegment}/pages/`,
                    icon: 'icon-lightbulb-03',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_authors',
                    title: t('layout.menuItems.authors'),
                    pathnames: [
                        `/domains/${domainSegment}/authors/`,
                        `/domains/${domainSegment}/authors/author/`,
                    ],
                    href: `/domains/${domainSegment}/authors/`,
                    icon: 'icon-n_author-performance-1',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_competitive_analysis',
                    title: t('layout.menuItems.competitive_analysis'),
                    href: `/domains/${domainSegment}/competitive-analysis/`,
                    icon: 'icon-list-ol',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_entity_clusters',
                    title: t('layout.menuItems.entity_clusters'),
                    href: `/domains/${domainSegment}/connected-entities/`,
                    icon: 'icon-network',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_algo_comparison',
                    title: t('layout.menuItems.algo_comparison'),
                    href: `/domains/${domainSegment}/algo-comparison/`,
                    icon: 'icon-scale-1',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_live_optimization_report',
                    title: t('layout.menuItems.live_optimization_report'),
                    href: `/domains/${domainSegment}/live-optimization-report/`,
                    icon: 'icon-scale-1',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_article_id_performance',
                    title: 'Article ID Performance',
                    href: `/domains/${domainSegment}/article-id-performance/`,
                    icon: 'icon-scale-1',
                    parent: 'discover_analytics',
                },
                {
                    id: 'discover_analytics_stats',
                    title: t('layout.menuItems.stats'),
                    icon: 'icon-position-distribution',
                    collapsTargetId: 'collapseDiscoverStats',
                    parent: 'discover_analytics',
                    children: [
                        {
                            id: 'discover_analytics_stats_stats',
                            title: t('layout.menuItems.stats'),
                            href: `/domains/${domainSegment}/stats/`,
                            icon: 'icon-area-chart',
                            parent: 'discover_analytics_stats',
                        },
                        {
                            id: 'discover_analytics_stats_search_appearances',
                            title: t('layout.menuItems.search_appearances'),
                            icon: 'icofont-binoculars',
                            href: `/domains/${domainSegment}/appearances/`,
                            parent: 'discover_analytics_stats',
                        },
                        {
                            id: 'discover_analytics_stats_countries',
                            title: t('layout.menuItems.countries'),
                            icon: 'icofont-globe',
                            href: `/domains/${domainSegment}/countries/`,
                            parent: 'discover_analytics_stats',
                        },
                    ],
                },
            ],
        },
        {
            id: 'search_analytics',
            title: t('layout.menuItems.search_analytics'),
            icon: 'icon-file-search-01-converted',
            collapsTargetId: 'collapseTwo',
            clearAllSelected: true,
            requiredDomain: true,
            children: [
                {
                    id: 'search_analytics_overview',
                    title: t('layout.menuItems.overview'),
                    href: `/domains/${domainSegment}/gsc-reports/`,
                    icon: 'icon-summary',
                    parent: 'search_analytics',
                },
                {
                    id: 'search_analytics_keywords',
                    title: t('layout.menuItems.keywords'),
                    icon: 'icon-key',
                    collapsTargetId: 'collapseKeywords',
                    parent: 'search_analytics',
                    children: [
                        {
                            id: 'search_analytics_keywords_keywords',
                            title: t('layout.menuItems.keywords'),
                            pathnames: [
                                `/domains/${domainSegment}/gsc-reports/keywords/`,
                                `/domains/${domainSegment}/gsc-reports/keywords/keyword/`,
                            ],
                            href: `/domains/${domainSegment}/gsc-reports/keywords/`,
                            icon: 'icon-keywords',
                            parent: 'search_analytics_keywords',
                        },
                        {
                            id: 'search_analytics_striking_distance',
                            title: t('layout.menuItems.striking_distance'),
                            icon: 'icon-striking_distance',
                            href: `/domains/${domainSegment}/gsc-reports/striking-distance/`,
                            parent: 'search_analytics_keywords',
                        },
                        {
                            id: 'search_analytics_keyword_clusters',
                            title: t('layout.menuItems.keyword_clusters'),
                            icon: 'icon-keyword_cluster',
                            pathnames: [
                                `/domains/${domainSegment}/gsc-reports/keyword-clusters/`,
                                `/domains/${domainSegment}/gsc-reports/keyword-clusters/cluster/`,
                            ],
                            href: `/domains/${domainSegment}/gsc-reports/keyword-clusters/`,
                            parent: 'search_analytics_keywords',
                        },
                        {
                            id: 'search_analytics_manual_keyword_lists',
                            title: t('layout.menuItems.manual_keyword_lists'),
                            icon: 'icon-striking_distance',
                            href: `/domains/${domainSegment}/gsc-reports/manual-keyword-list/`,
                            parent: 'search_analytics_keywords',
                        },
                    ],
                },
                {
                    id: 'search_analytics_urls',
                    title: t('layout.menuItems.urls'),
                    icon: 'icon-link-01',
                    collapsTargetId: 'collapseURLs',
                    parent: 'search_analytics',
                    requiredDomain: true,
                    children: [
                        {
                            id: 'search_analytics_urls_urls',
                            title: t('layout.menuItems.urls'),
                            pathnames: [
                                `/domains/${domainSegment}/gsc-reports/urls/`,
                                `/domains/${domainSegment}/gsc-reports/urls/url/`,
                            ],
                            href: `/domains/${domainSegment}/gsc-reports/urls/`,
                            icon: 'icon-urls',
                            parent: 'search_analytics_urls',
                        },
                        {
                            id: 'search_analytics_urls_directories',
                            title: t('layout.menuItems.directories'),
                            icon: 'icon-directories',
                            pathnames: [
                                `/domains/${domainSegment}/gsc-reports/directories/`,
                                `/domains/${domainSegment}/gsc-reports/directories/directory/`,
                            ],
                            href: `/domains/${domainSegment}/gsc-reports/directories/`,
                            parent: 'search_analytics_urls',
                        },
                        {
                            id: 'search_analytics_urls_subdomains',
                            title: t('layout.menuItems.subdomains'),
                            icon: 'icon-subdomains',
                            pathnames: [
                                `/domains/${domainSegment}/gsc-reports/subdomains/`,
                                `/domains/${domainSegment}/gsc-reports/subdomains/subdomain/`,
                            ],
                            href: `/domains/${domainSegment}/gsc-reports/subdomains/`,
                            parent: 'search_analytics_urls',
                        },
                        {
                            id: 'search_analytics_urls_manual_URL_lists',
                            title: t('layout.menuItems.manual_url_lists'),
                            icon: 'icofont-globe',
                            pathnames: [
                                `/domains/${domainSegment}/gsc-reports/manual-url-list/`,
                                `/domains/${domainSegment}/gsc-reports/manual-url-list/cluster/`,
                            ],
                            href: `/domains/${domainSegment}/gsc-reports/manual-url-list/`,
                            parent: 'search_analytics_urls',
                        },
                    ],
                },
                {
                    id: 'search_analytics_evergreen',
                    title: t('layout.menuItems.evergreen'),
                    icon: 'icon-infinity',
                    collapsTargetId: 'collapseEvergreen',
                    parent: 'search_analytics',
                    children: [
                        {
                            id: 'search_analytics_evergreen_url',
                            title: t('layout.menuItems.evergreen_urls'),
                            href: `/domains/${domainSegment}/gsc-reports/evergreen/`,
                            icon: 'icon-evergreen',
                            parent: 'search_analytics_evergreen',
                        },
                        {
                            id: 'search_analytics_evergreen_list_keywords',
                            title: t('layout.menuItems.list_keywords'),
                            icon: 'icon-best_keywords',
                            href: `/domains/${domainSegment}/gsc-reports/best-keywords/`,
                            parent: 'search_analytics_evergreen',
                        },
                        {
                            id: 'search_analytics_evergreen_year',
                            title: t('layout.menuItems.year_keywords'),
                            icon: 'icon-yers_keywords',
                            href: `/domains/${domainSegment}/gsc-reports/year-keywords/`,
                            parent: 'search_analytics_evergreen',
                        },
                        {
                            id: 'search_analytics_evergreen_questions_keywords',
                            title: t('layout.menuItems.questions_keywords'),
                            icon: 'icon-questions',
                            href: `/domains/${domainSegment}/gsc-reports/questions-keywords/`,
                            parent: 'search_analytics_evergreen',
                        },
                        {
                            id: 'search_analytics_evergreen_custom_keywords',
                            title: t('layout.menuItems.custom_keywords'),
                            icon: 'icon-seasons',
                            href: `/domains/${domainSegment}/gsc-reports/custom-keywords/`,
                            parent: 'search_analytics_evergreen',
                        },
                    ],
                },
                {
                    id: 'search_analytics_algo_comparison',
                    title: t('layout.menuItems.algo_comparison'),
                    icon: 'icon-scale-1',
                    parent: 'search_analytics',
                    pathnames: [`/domains/${domainSegment}/gsc-reports/algo-comparison/`],
                    href: `/domains/${domainSegment}/gsc-reports/algo-comparison/?view=overall_performance`,
                },
                {
                    id: 'googleSurfaces',
                    title: t('layout.menuItems.google_surfaces'),
                    icon: 'icon-google-outline-svgrepo-com-1-converted-1',
                    href: `/domains/${domainSegment}/google-surfaces/`,
                },
                {
                    id: 'search_analytics_stats',
                    title: t('layout.menuItems.stats'),
                    icon: 'icon-bar-chart-10',
                    collapsTargetId: 'collapseStats',
                    parent: 'search_analytics',
                    children: [
                        {
                            id: 'search_analytics_stats_position_distribution',
                            title: t('layout.menuItems.position_distribution'),
                            href: `/domains/${domainSegment}/gsc-reports/position-distribution/`,
                            icon: 'icon-position-distribution',
                            parent: 'search_analytics_stats',
                        },
                        {
                            id: 'search_analytics_stats_ctr-per-position',
                            title: t('layout.menuItems.ctr_per_position'),
                            icon: 'icon-ctr1',
                            href: `/domains/${domainSegment}/gsc-reports/ctr-per-position/`,
                            parent: 'search_analytics_stats',
                        },
                        {
                            id: 'search_analytics_stats_search-appearance',
                            title: t('layout.menuItems.search_appearance'),
                            icon: 'icon-appearances',
                            href: `/domains/${domainSegment}/gsc-reports/search-appearance/`,
                            parent: 'search_analytics_stats',
                        },
                        {
                            id: 'search_analytics_stats_countries',
                            title: t('layout.menuItems.countries'),
                            icon: 'icofont-globe',
                            href: `/domains/${domainSegment}/gsc-reports/countries/`,
                            parent: 'search_analytics_stats',
                        },
                    ],
                },
            ],
        },
        {
            id: 'opportunities',
            title: t('layout.menuItems.opportunities'),
            collapsTargetId: 'collapseOpportunity',
            icon: 'icon-target-04-converted',
            clearAllSelected: true,
            requiredDomain: true,
            children: [
                {
                    id: 'opportunities_SEO_Recommendations',
                    title: t('layout.menuItems.search_recommendations'),
                    pathnames: [`/domains/${domainSegment}/opportunities/search/`],
                    href: `/domains/${domainSegment}/opportunities/search/?view=striking_distance`,
                    icon: 'icon-compass-03',
                    parent: 'opportunities',
                },
                {
                    id: 'opportunities_Discover',
                    title: t('layout.menuItems.discover_recommendations'),
                    href: `/domains/${domainSegment}/opportunities/recommendations/`,
                    icon: 'icon-file-search',
                    parent: 'opportunities',
                },
            ],
        },
        // {
        //     id: 'discover_tracking',
        //     title: t('layout.menuItems.discover_tracking'),
        //     icon: 'icon-compass-03-converted ',
        //     collapsTargetId: 'collapseDiscoverTracking',
        //     clearAllSelected: true,
        //     children: [
        //         {
        //             id: 'discover_tracking_entity',
        //             title: t('layout.menuItems.discover_entity_tracking'),
        //             href: `/tools/google-discover-data/`,
        //             icon: 'icon-algo',
        //             parent: 'discover_tracking',
        //             hrefStartsWith: '/tools/google-discover-data/',
        //         },
        //     ],
        // },
        // The GSC tools nav is the full planned report set, grouped exactly as the tools
        // themselves are grouped. Only the reports that exist carry an `href`; the rest are
        // declared with `disabled: true`, which Sidebar / TopMenu render greyed out and
        // unclickable. Replace the flag with the report's `href` as each one lands.
        {
            id: 'tools',
            title: t('layout.menuItems.tools'),
            collapsTargetId: 'collapseTools',
            clearAllSelected: true,
            icon: 'icon-200',
            children: [
                {
                    id: 'tools_overview',
                    title: t('layout.menuItems.gsc_tools.overview'),
                    icon: 'icon-pie-chart',
                    collapsTargetId: 'collapseToolsOverview',
                    parent: 'tools',
                    children: [
                        {
                            // 01
                            id: 'tools_my_sites',
                            title: t('layout.menuItems.gsc_tools.my_sites'),
                            href: '/domains/tools/domain-performance/',
                            icon: 'icon-website',
                            parent: 'tools_overview',
                        },
                        {
                            // 02
                            id: 'tools_search_overview',
                            title: t('layout.menuItems.gsc_tools.search_overview'),
                            href: '/domains/tools/search-overview/',
                            icon: 'icon-search',
                            parent: 'tools_overview',
                        },
                        {
                            // Predates the numbered report set and has no slot in it, but the
                            // report exists and stays reachable.
                            id: 'tools_explorer',
                            title: t('layout.menuItems.url_explorer'),
                            href: '/domains/tools/live-url/',
                            icon: 'icon-urls',
                            parent: 'tools_overview',
                        },
                        {
                            // 07
                            id: 'tools_latest_24_hours',
                            title: t('layout.menuItems.gsc_tools.latest_24_hours'),
                            href: '/domains/tools/latest-hours/',
                            icon: 'icon-history',
                            parent: 'tools_overview',
                        },
                        {
                            // 08
                            id: 'tools_discover_and_news',
                            title: t('layout.menuItems.gsc_tools.discover_and_news'),
                            href: '/domains/tools/discover-news/',
                            icon: 'icon-compass-03',
                            parent: 'tools_overview',
                        },
                    ],
                },
                {
                    id: 'tools_search_performance',
                    title: t('layout.menuItems.gsc_tools.search_performance'),
                    icon: 'icon-bar-chart-10',
                    collapsTargetId: 'collapseToolsSearchPerformance',
                    parent: 'tools',
                    children: [
                        {
                            // 03
                            id: 'tools_pages_queries_changes',
                            title: t('layout.menuItems.gsc_tools.pages_queries_changes'),
                            href: '/domains/tools/pages-queries-changes/',
                            icon: 'icon-pages',
                            parent: 'tools_search_performance',
                        },
                        {
                            // 05
                            id: 'tools_countries_devices_appearance',
                            title: t('layout.menuItems.gsc_tools.countries_devices_appearance'),
                            href: '/domains/tools/audience-appearance/',
                            icon: 'icon-globe',
                            parent: 'tools_search_performance',
                        },
                        {
                            // 06
                            id: 'tools_brand_vs_non_brand',
                            title: t('layout.menuItems.gsc_tools.brand_vs_non_brand'),
                            href: '/domains/tools/brand-non-brand/',
                            icon: 'icon-scale-1',
                            parent: 'tools_search_performance',
                        },
                        {
                            // 15
                            id: 'tools_query_visibility_positions',
                            title: t('layout.menuItems.gsc_tools.query_visibility_positions'),
                            disabled: true,
                            icon: 'icon-position-distribution',
                            parent: 'tools_search_performance',
                        },
                    ],
                },
                {
                    id: 'tools_content_analysis',
                    title: t('layout.menuItems.gsc_tools.content_analysis'),
                    icon: 'icon-file-04',
                    collapsTargetId: 'collapseToolsContentAnalysis',
                    parent: 'tools',
                    children: [
                        {
                            // 11
                            id: 'tools_content_groups',
                            title: t('layout.menuItems.gsc_tools.content_groups'),
                            href: '/domains/tools/content-groups/',
                            icon: 'icon-fi_grid',
                            parent: 'tools_content_analysis',
                        },
                        {
                            // 12
                            id: 'tools_query_topics',
                            title: t('layout.menuItems.gsc_tools.query_topics'),
                            href: '/domains/tools/topic-clusters/',
                            icon: 'icon-keyword_cluster',
                            parent: 'tools_content_analysis',
                        },
                        {
                            // 14
                            id: 'tools_content_performance_heatmap',
                            title: t('layout.menuItems.gsc_tools.content_performance_heatmap'),
                            disabled: true,
                            icon: 'icon-grid-03',
                            parent: 'tools_content_analysis',
                        },
                        {
                            // 16
                            id: 'tools_site_changes_performance',
                            title: t('layout.menuItems.gsc_tools.site_changes_performance'),
                            disabled: true,
                            icon: 'icon-area-chart',
                            parent: 'tools_content_analysis',
                        },
                    ],
                },
                {
                    id: 'tools_opportunities',
                    title: t('layout.menuItems.gsc_tools.opportunities'),
                    icon: 'icon-target-04-converted',
                    collapsTargetId: 'collapseToolsOpportunities',
                    parent: 'tools',
                    children: [
                        {
                            // 04
                            id: 'tools_search_opportunities',
                            title: t('layout.menuItems.gsc_tools.search_opportunities'),
                            href: '/domains/tools/search-opportunities/',
                            icon: 'icon-striking_distance',
                            parent: 'tools_opportunities',
                        },
                        {
                            // 13
                            id: 'tools_competing_pages',
                            title: t('layout.menuItems.gsc_tools.competing_pages'),
                            href: '/domains/tools/cannibalization-review/',
                            icon: 'icon-list-ol',
                            parent: 'tools_opportunities',
                        },
                        {
                            // 17
                            id: 'tools_ctr_benchmark',
                            title: t('layout.menuItems.gsc_tools.ctr_benchmark'),
                            disabled: true,
                            icon: 'icon-ctr1',
                            parent: 'tools_opportunities',
                        },
                        {
                            // 19
                            id: 'tools_opportunity_shortlist',
                            title: t('layout.menuItems.gsc_tools.opportunity_shortlist'),
                            disabled: true,
                            icon: 'icon-list',
                            parent: 'tools_opportunities',
                        },
                    ],
                },
                {
                    id: 'tools_indexing_sitemaps',
                    title: t('layout.menuItems.gsc_tools.indexing_sitemaps'),
                    icon: 'icon-file-search',
                    collapsTargetId: 'collapseToolsIndexingSitemaps',
                    parent: 'tools',
                    children: [
                        {
                            // 09
                            id: 'tools_url_inspection',
                            title: t('layout.menuItems.gsc_tools.url_inspection'),
                            href: '/domains/tools/url-inspection/',
                            icon: 'icon-fi_search',
                            parent: 'tools_indexing_sitemaps',
                        },
                        {
                            // 10
                            id: 'tools_sitemap_status',
                            title: t('layout.menuItems.gsc_tools.sitemap_status'),
                            href: '/domains/tools/sitemap-status/',
                            icon: 'icon-account-tree',
                            parent: 'tools_indexing_sitemaps',
                        },
                    ],
                },
                {
                    // Optional add-on group: these reports are researched outside Search Console.
                    id: 'tools_external_research',
                    title: t('layout.menuItems.gsc_tools.external_research'),
                    icon: 'icon-binoculars',
                    collapsTargetId: 'collapseToolsExternalResearch',
                    parent: 'tools',
                    children: [
                        {
                            // 20
                            id: 'tools_keyword_research',
                            title: t('layout.menuItems.gsc_tools.keyword_research'),
                            disabled: true,
                            icon: 'icon-keywords',
                            parent: 'tools_external_research',
                        },
                        {
                            // 21
                            id: 'tools_competitor_research',
                            title: t('layout.menuItems.gsc_tools.competitor_research'),
                            disabled: true,
                            icon: 'icon-intersect-circle',
                            parent: 'tools_external_research',
                        },
                        {
                            // 22
                            id: 'tools_backlinks',
                            title: t('layout.menuItems.gsc_tools.backlinks'),
                            disabled: true,
                            icon: 'icon-link-01',
                            parent: 'tools_external_research',
                        },
                        {
                            // 23
                            id: 'tools_technical_site_audit',
                            title: t('layout.menuItems.gsc_tools.technical_site_audit'),
                            disabled: true,
                            icon: 'icon-check-icon',
                            parent: 'tools_external_research',
                        },
                        {
                            // 24
                            id: 'tools_ai_visibility',
                            title: t('layout.menuItems.gsc_tools.ai_visibility'),
                            disabled: true,
                            icon: 'icon-ai-tracking',
                            parent: 'tools_external_research',
                        },
                    ],
                },
                {
                    // 25 — the group's bottom utility link, so it sits outside the groups above.
                    id: 'tools_settings_data_controls',
                    title: t('layout.menuItems.gsc_tools.settings_data_controls'),
                    disabled: true,
                    icon: 'icon-settings1',
                    parent: 'tools',
                },
            ],
        },
        {
            id: 'commerce',
            title: t('layout.menuItems.commerce'),
            clearAllSelected: true,
            href: '/commerce/',
            hrefStartsWith: '/commerce/',
            icon: 'icon-algo',
        },
    ];

    // Without a domain every domain-scoped link would resolve to `/domains/null/...`, so point
    // all of them at the settings page instead.
    if (!domainId) data = redirectDomainLinks(data);

    // if (!domainId) data = data.filter((item) => !item.requiredDomain);

    // function flattenByTitle(data) {
    //     const result = {};

    //     function recurse(items) {
    //         for (const item of items) {
    //             if (item.title) {
    //                 result[item.title] = item.title;
    //             }
    //             if (item.children) {
    //                 recurse(item.children);
    //             }
    //         }
    //     }

    //     recurse(data);
    //     return result;
    // }

    // const titleObjectMap = flattenByTitle(data);

    return data;
};
